/* eslint-disable camelcase */
import React, { useState, useContext, useEffect } from "react"
import { useQueryClient, useQuery } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  Autocomplete,
  Divider,
  Button,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import Save from "@mui/icons-material/Save"
import PersonSearchIcon from "@mui/icons-material/PersonSearch"
import AddCardIcon from "@mui/icons-material/AddCard"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import DeleteIcon from "@mui/icons-material/Delete"
import SearchIcon from "@mui/icons-material/Search"

// SIAC Components & Context
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { GlobalContext } from "../../../contexts/GlobalContext"
import { useMutation, api, showWarning } from "../../../api"
import getIconComponent from "../../utils/getIconComponent"
import dayjs from "dayjs"

// Importar los componentes desarrollados previamente
import ModalBuscarDeudas from "./components/ModalBuscarDeudas"
import TablaPlanillaCobro from "./components/TablaPlanillaCobro"
import ModalFormasPago from "./components/ModalFormasPago"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

// Helper monetario
const formatMoneda = (valor) => {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(valor || 0)
}

const CrearCobrosVista = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // =========================================================================
  // PASO 1: ESTADOS GLOBALES DE LA PANTALLA
  // =========================================================================

  // Estado de Cabecera del Cobro (Manejo de Estado)
  const [cabecera, setCabecera] = useState({
    clicodigo: "",
    clinombre: "",
    cxcfecemi: dayjs().format("YYYY-MM-DD"),
    cxcdetalle: "",
    cxccambio: 1.0,
    seqcodigo: "", // Inicializado vacío, se llenará dinámicamente
    cjacodigo: "", // Inicializado vacío, se llenará dinámicamente
    reccodigo: "", // Inicializado vacío, se llenará dinámicamente
  })

  // Estado de Búsqueda de Clientes
  const [busquedaCliente, setBusquedaCliente] = useState("")
  const [listaClientes, setListaClientes] = useState([])
  const [isBuscandoClientes, setIsBuscandoClientes] = useState(false)

  // Estructuras Transaccionales
  const [facturasCobrar, setFacturasCobrar] = useState([])
  const [pagosAgregados, setPagosAgregados] = useState([])

  // Estados para Modales
  const [modalDeudasOpen, setModalDeudasOpen] = useState(false)
  const [modalPagosOpen, setModalPagosOpen] = useState(false)

  // =========================================================================
  // CONSULTA DE CATÁLOGOS DE CABECERA (Secuencias, Cajas, Recaudadores)
  // =========================================================================
  const { data: rawCabeceraCat, isFetching: isFetchingCabeceraCat } = useQuery({
    queryKey: ["getCatalogosCabeceraCobro"],
    queryFn: async () => {
      try {
        const res = await api.post("/CobrosVista/getCatalogosCabeceraCobro", {})
        return res?.data?.data || res?.data || res || {}
      } catch (error) {
        console.error("Error al obtener catálogos de cabecera", error)
        return {}
      }
    },
    refetchOnWindowFocus: false,
  })

  // Extracción Defensiva de arreglos para los Combos de la Cabecera
  const getSafeArray = (key, dataObj) => {
    if (Array.isArray(dataObj[key])) return dataObj[key]
    if (dataObj.data && Array.isArray(dataObj.data[key])) return dataObj.data[key]
    return []
  }

  const cabeceraCat = rawCabeceraCat || {}
  const secuenciasLista = getSafeArray("secuencias", cabeceraCat)
  const cajasLista = getSafeArray("cajas", cabeceraCat)
  const recaudadoresLista = getSafeArray("recaudadores", cabeceraCat)

  // Efecto para autoseleccionar el primer valor de los combos si están vacíos
  useEffect(() => {
    setCabecera((prev) => {
      const updated = { ...prev }
      let changed = false
      if (secuenciasLista.length > 0 && !prev.seqcodigo) {
        updated.seqcodigo = secuenciasLista[0].id
        changed = true
      }
      if (cajasLista.length > 0 && !prev.cjacodigo) {
        updated.cjacodigo = cajasLista[0].id
        changed = true
      }
      if (recaudadoresLista.length > 0 && !prev.reccodigo) {
        updated.reccodigo = recaudadoresLista[0].id
        changed = true
      }
      return changed ? updated : prev
    })
  }, [secuenciasLista, cajasLista, recaudadoresLista])

  // =========================================================================
  // LÓGICA DE BÚSQUEDA DE CLIENTES EN VIVO
  // =========================================================================
  useEffect(() => {
    const fetchClientes = async () => {
      if (busquedaCliente.trim().length < 3) {
        setListaClientes([])
        return
      }
      setIsBuscandoClientes(true)
      try {
        const response = await api.post("/CobrosVista/getClientesCartera", {
          page: 1,
          perPage: 30,
          externalFilters: { busqueda: busquedaCliente },
        })

        // PASO 2: EXTRACCIÓN DEFENSIVA EN REACT QUERY
        const rawData = response?.data?.data || response?.data || response || []
        const listaValidada = Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : []

        const opcionesFormat = listaValidada.map((c) => ({
          id: c.clicodigo,
          label: `${c.clicodigo} - ${c.clinombre} (RUC: ${c.cliruc || "N/A"})`,
          clinombre: c.clinombre,
          ...c,
        }))
        setListaClientes(opcionesFormat)
      } catch (error) {
        console.error("Error buscando clientes:", error)
      } finally {
        setIsBuscandoClientes(false)
      }
    }

    const delayDebounceFn = setTimeout(() => fetchClientes(), 600)
    return () => clearTimeout(delayDebounceFn)
  }, [busquedaCliente])

  // =========================================================================
  // CÁLCULOS FINANCIEROS Y DE CUADRE
  // =========================================================================
  const totalACobrar = facturasCobrar.reduce(
    (acc, fac) => acc + Number(fac.abono || 0) + Number(fac.mora_dinamica || 0),
    0,
  )
  const totalPagadoFormas = pagosAgregados.reduce((acc, pag) => acc + Number(pag.valor_real || 0), 0)
  const diferenciaCuadre = totalACobrar - totalPagadoFormas

  const handleAddDeudas = (deudasSeleccionadas) => {
    const deudasNuevas = deudasSeleccionadas.filter(
      (nd) => !facturasCobrar.some((fc) => fc.facnumfac === nd.facnumfac && fc.facsecuen === nd.facsecuen),
    )
    const deudasFormateadas = deudasNuevas.map((d) => ({
      ...d,
      abono: d.saldo_neto,
      mora_dinamica: d.mora_calculada,
    }))
    setFacturasCobrar([...facturasCobrar, ...deudasFormateadas])
  }

  // =========================================================================
  // MUTACIÓN PARA GRABAR
  // =========================================================================
  const { mutateAsync: SaveCobro, isPending: isSavingCobro } = useMutation({
    queryKey: ["isSavingCobro"],
    fn: async (payload) => {
      const response = await api.post("/CobrosVista/saveCobroTransaccional", payload)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await queryClient.invalidateQueries()
      navigate(-1)
    },
  })

  const handleGuardar = async (e) => {
    if (e) e.preventDefault()

    if (!cabecera.clicodigo) return showWarning("Debe seleccionar un Cliente para procesar el cobro.")
    if (!cabecera.cxcdetalle) return showWarning("El Detalle del cobro es obligatorio.")
    if (!cabecera.seqcodigo) return showWarning("Debe seleccionar una Secuencia de Cobro válida.")
    if (!cabecera.cjacodigo) return showWarning("Debe seleccionar una Caja válida.")
    if (!cabecera.reccodigo) return showWarning("Debe seleccionar un Recaudador válido.")
    if (facturasCobrar.length === 0) return showWarning("Cobre una Factura por lo menos.")
    if (pagosAgregados.length === 0 && totalACobrar > 0) return showWarning("Debe ingresar al menos una Forma de Pago.")

    if (Math.abs(diferenciaCuadre) > 0.01) {
      return showWarning(
        `Existe una diferencia de ${formatMoneda(diferenciaCuadre)}. El total a cobrar debe ser cubierto exactamente por las formas de pago.`,
      )
    }

    const payloadTransaccional = {
      cabecera: { ...cabecera, total_cobro: totalACobrar },
      facturas: facturasCobrar,
      pagos: pagosAgregados,
    }

    await SaveCobro(payloadTransaccional)
  }

  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  const toolbarActions = guardarAction
    ? [
        {
          label: guardarAction.acccaption,
          key: guardarAction.acccaption,
          icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
        },
      ]
    : [{ label: "Grabar Cobro", key: "GUARDAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleGuardar}
                disabled={isSavingCobro || isFetchingCabeceraCat}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 20px 30px",
            fontSize: "25px",
            color: "#196C87",
          }}
        >
          <AccountBalanceWalletIcon sx={{ fontSize: 35, mr: 1 }} />
          <b>Registro de Cobros a la Vista</b>
        </div>

        {/* Añadido isFetchingCabeceraCat al CustomBackdrop para bloquear mientras cargan los catálogos */}
        <CustomBackdrop isLoading={isSavingCobro || isBuscandoClientes || isFetchingCabeceraCat} />

        <Box sx={StyledRoot}>
          {/* ======================= SECCIÓN 1: CABECERA Y BÚSQUEDA DE CLIENTE ======================= */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom>
              1. Selección de Cliente y Configuración del Comprobante
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  // PASO 3: CONFIGURACIÓN ESTRICTA
                  options={listaClientes}
                  getOptionLabel={(option) => option.label || ""}
                  loading={isBuscandoClientes}
                  // OBLIGATORIO: Mantener la selección visual atada al estado
                  value={
                    cabecera.clicodigo
                      ? { id: cabecera.clicodigo, label: `${cabecera.clicodigo} - ${cabecera.clinombre}` }
                      : null
                  }
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  onInputChange={(event, newInputValue, reason) => {
                    // Solo buscar si el usuario está tipeando (Evita resetear el combo al seleccionar)
                    if (reason === "input") {
                      setBusquedaCliente(newInputValue)
                    }
                  }}
                  onChange={(event, newValue) => {
                    // PASO 4: Limpieza de dependencias y actualización segura
                    setCabecera((prev) => ({
                      ...prev,
                      clicodigo: newValue ? newValue.id : "",
                      clinombre: newValue ? newValue.clinombre : "",
                    }))
                    setFacturasCobrar([])
                    setPagosAgregados([])
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar Cliente por Código, RUC o Nombre *"
                      variant="outlined"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <PersonSearchIcon color="action" sx={{ ml: 1, mr: 1 }} />,
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Concepto / Detalle del Cobro *"
                  size="small"
                  value={cabecera.cxcdetalle}
                  onChange={(e) => setCabecera({ ...cabecera, cxcdetalle: e.target.value.toUpperCase() })}
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>

              {/* COMBOS DINÁMICOS CON LOS CATÁLOGOS DEL BACKEND */}
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Secuencia de Cobro *"
                  size="small"
                  value={cabecera.seqcodigo}
                  onChange={(e) => setCabecera({ ...cabecera, seqcodigo: e.target.value })}
                >
                  {secuenciasLista.length === 0 && <MenuItem value="">Sin Secuencias Configuradas</MenuItem>}
                  {secuenciasLista.map((op) => (
                    <MenuItem key={op.id} value={op.id}>
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Caja de Recaudación *"
                  size="small"
                  value={cabecera.cjacodigo}
                  onChange={(e) => setCabecera({ ...cabecera, cjacodigo: e.target.value })}
                >
                  {cajasLista.length === 0 && <MenuItem value="">Sin Cajas Asignadas</MenuItem>}
                  {cajasLista.map((op) => (
                    <MenuItem key={op.id} value={op.id}>
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Recaudador Asignado *"
                  size="small"
                  value={cabecera.reccodigo}
                  onChange={(e) => setCabecera({ ...cabecera, reccodigo: e.target.value })}
                >
                  {recaudadoresLista.length === 0 && <MenuItem value="">Sin Recaudadores</MenuItem>}
                  {recaudadoresLista.map((op) => (
                    <MenuItem key={op.id} value={op.id}>
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          {/* ======================= SECCIÓN 2: PLANILLA DE COBRO ======================= */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" color="primary" fontWeight="bold">
                2. Planilla de Deudas a Cobrar
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SearchIcon />}
                disabled={!cabecera.clicodigo}
                onClick={() => setModalDeudasOpen(true)}
              >
                Buscar Deudas Pendientes
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <TablaPlanillaCobro facturasCobrar={facturasCobrar} setFacturasCobrar={setFacturasCobrar} />
          </Paper>

          {/* ======================= SECCIÓN 3: FORMAS DE PAGO Y RESUMEN FINANCIERO ======================= */}
          <Grid container spacing={3}>
            {/* PANEL IZQUIERDO: FORMAS DE PAGO */}
            <Grid item xs={12} md={8}>
              <Paper elevation={2} sx={{ p: 3, height: "100%", borderRadius: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" color="primary" fontWeight="bold">
                    3. Formas de Pago Ingresadas
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddCardIcon />}
                    disabled={facturasCobrar.length === 0}
                    onClick={() => setModalPagosOpen(true)}
                  >
                    Ingresar Nuevo Pago
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {pagosAgregados.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 3 }}>
                    Aún no ha ingresado ninguna forma de pago.
                  </Typography>
                ) : (
                  <TableContainer
                    component={Box}
                    sx={{ maxHeight: 250, overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ "& th": { bgcolor: "#f5f5f5", fontWeight: "bold" } }}>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Referencia</TableCell>
                          <TableCell align="right">Valor</TableCell>
                          <TableCell align="center">Quitar</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagosAgregados.map((p, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{p.tipo_pago_desc}</TableCell>
                            <TableCell>{p.cheque_transf || p.cuenta_referencia || "N/A"}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: "bold" }}>
                              {formatMoneda(p.valor_real)}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setPagosAgregados(pagosAgregados.filter((_, i) => i !== idx))
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>

            {/* PANEL DERECHO: PANEL FINANCIERO (CUADRE DE CAJA) */}
            <Grid item xs={12} md={4}>
              <Paper elevation={4} sx={{ p: 3, height: "100%", borderRadius: 2, bgcolor: "#fafafa" }}>
                <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom align="center">
                  Resumen de Liquidación
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body1" fontWeight="bold">
                    Valor a Cobrar:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {formatMoneda(totalACobrar)}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Total Ingresado en Pagos:
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatMoneda(totalPagadoFormas)}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={1.5}
                  borderRadius={1}
                  bgcolor={Math.abs(diferenciaCuadre) > 0.01 ? "#ffebee" : "#e8f5e9"}
                  border={`1px solid ${Math.abs(diferenciaCuadre) > 0.01 ? "#ef5350" : "#66bb6a"}`}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color={Math.abs(diferenciaCuadre) > 0.01 ? "error" : "success.main"}
                  >
                    {diferenciaCuadre > 0.01 ? "FALTANTE:" : diferenciaCuadre < -0.01 ? "SOBRANTE:" : "CUADRADO:"}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={Math.abs(diferenciaCuadre) > 0.01 ? "error" : "success.main"}
                  >
                    {formatMoneda(Math.abs(diferenciaCuadre))}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </div>

      {/* Instanciación de Modales */}
      <ModalBuscarDeudas
        open={modalDeudasOpen}
        onClose={() => setModalDeudasOpen(false)}
        clicodigo={cabecera.clicodigo}
        onAddDeudas={handleAddDeudas}
      />

      <ModalFormasPago
        open={modalPagosOpen}
        onClose={() => setModalPagosOpen(false)}
        diferenciaCuadre={diferenciaCuadre}
        onAddPago={(pago) => setPagosAgregados([...pagosAgregados, pago])}
        facturasCobrar={facturasCobrar}
      />
    </ThemeProvider>
  )
}

export default CrearCobrosVista
